<script setup>
  import { Button } from "@/components/ui/button";
  import { computed, ref, watch, onMounted, onUnmounted, provide } from "vue";
  import { ChevronDown, Download } from "lucide-vue-next";
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger,
  } from "@/components/ui/dropdown-menu";
  import { Tooltip } from "@/components/ui/tooltip";
  import { useChartDisplay } from "@/composables/useChartDisplay";
  import { languageDirection } from "@/utils/textDirection";
  import useMyFetch from "@stores/useMyFetch";
  import { useSurveyStore } from "@stores/survey/survey";
  import { useTaskProgressesStore } from "@/stores/taskProgresses";
  import { getClientId } from "@utils/clientId";
  import { useVerbatims } from "@/composables/useVerbatims";
  import { Select } from "@/components/episto-ui/select";
  import ResultsBarChart from "../Charts/ResultsBarChart.vue";
  import ResultsStackedChart from "../Charts/ResultsStackedChart.vue";
  import ResultsPieChart from "../Charts/ResultsPieChart.vue";
  import ResultsHeatmapChart from "../Charts/ResultsHeatmapChart.vue";
  import ResultsRadarChart from "../Charts/ResultsRadarChart.vue";
  import ResultsPodiumChart from "../Charts/ResultsPodiumChart.vue";
  import ResultsDotPlotChart from "../Charts/ResultsDotPlotChart.vue";
  import ResultsDataTable from "../Charts/ResultsDataTable.vue";
  import ResultsVerbatimsList from "../Charts/ResultsVerbatimsList.vue";
  import ResultsVerbatimsLanguageToggle from "../Charts/ResultsVerbatimsLanguageToggle.vue";
  import ResultsVerbatimSummary from "../Charts/ResultsVerbatimSummary.vue";
  import ResultsNumericTable from "../Charts/ResultsNumericTable.vue";
  import ResultsThemesForm from "../Charts/ResultsThemesForm.vue";
  import ResultsStyledBlock from "../ResultsStyledBlock.vue";
  import { NPS_BUCKETS } from "../constants";
  import ResultsV2CardIndicators from "./ResultsV2CardIndicators.vue";
  import CardTypeIcon from "./CardTypeIcon.vue";
  import PopulationSwitch from "./PopulationSwitch.vue";
  import ResultsCardToolbar from "./ResultsCardToolbar.vue";
  import { LoadingState } from "@/components/episto-ui/loading-state";
  import { isHalfWidthCard } from "../constants";
  import { useResultsV2CardsStore } from "@stores/survey/resultsV2/resultsV2.cards";
  import { useResultsV2RecodesStore } from "@stores/survey/resultsV2/resultsV2.recodes";
  import { useResultsV2PopulationsStore } from "@stores/survey/resultsV2/resultsV2.populations";
  import { useSurveyLocalesStore } from "@stores/survey/surveyLocales";
  import { useCardTypeIdentity } from "@/composables/useCardTypeIdentity";
  import { storeToRefs } from "pinia";
  import { defineAsyncComponent } from "vue";
  import { removeHtmlMarkdownAndLineBreak } from "@utils/toolBox";
  import { captureEvent } from "@/lib/analytics";
  import AiStar from "@/components/episto-ui/ai/AiStar.vue";
  const RecodeOpenEndForm = defineAsyncComponent(() => import("../RecodeOpenEndForm.vue"));

  const cardsStore = useResultsV2CardsStore();
  const { taskProgresses } = storeToRefs(useTaskProgressesStore());
  const populationsStore = useResultsV2PopulationsStore();
  const { populations: viewPopulations } = storeToRefs(populationsStore);

  const props = defineProps({
    card: { type: Object, required: true },
    readonly: { type: Boolean, default: false },
  });
  const { canAnalyzeSurvey } = storeToRefs(useSurveyStore());

  const recodesStore = useResultsV2RecodesStore();

  function closeConfig() {
    cardsStore.closeConfig(props.card.id);
  }

  function handleConfigSave() {
    closeConfig();
    if (isRecodeQuestion.value) {
      store.markCardRegenerating(props.card.card_id);
      startCodificationPolling({ optimistic: true });
    } else {
      store.refreshChartsData();
    }
  }

  function handleConfigCancel() {
    const token = questionMeta.value?.token;
    if (token) recodesStore.cleanup(token);
    closeConfig();
  }

  async function handleConfigDelete() {
    const token = questionMeta.value?.token;
    if (!token) return;
    const ok = await recodesStore.deleteRecode(token);
    if (ok) {
      closeConfig();
      store.refreshChartsData();
    }
  }

  const cardRef = computed(() => props.card);
  const {
    store,
    mergedOptions,
    questionMeta,
    effectiveQuestionId,
    cardType,
    populationsBarData,
    isMatrix,
    matrixItemsBarData,
    stackedData,
    matrixChoiceCodes,
    matrixChoiceLabels,
    pieData,
    radarAxes,
    radarSeries,
    radarScale,
    matrixTableData,
    respondents,
    populationRespondents,
    displayedPopulations,
    singlePopulation,
    shouldIncludeWeights,
    activeIteration,
    loopIterationOptions,
    showSentiment,
    isNumeric,
    numericStats,
    downloadCardAsImage,
    copyCardToClipboard,
    copied,
    customBaseLabel,
    isBar,
    isStacked,
    isNpsChartType,
    isPie,
    isHorizontal,
    isDoughnut,
    isHeatmap,
    isRadar,
    isPodium,
    isDotPlot,
    isSingleSeries,
    showSignificance,
    cardComment,
    displayName,
    displayTitle,
    displayMode: chartDisplayMode,
  } = useChartDisplay(cardRef);
  provide("cardPopulations", displayedPopulations);

  const textDirection = computed(() => languageDirection(store.displayLanguage));

  const {
    isOpenQuestion,
    isOpenRecode,
    isRecodeQuestion,
    isMediaQuestion,
    hasVerbatims,
    verbatimsQuestionId,
    recodeQuestionId,
    otherQuestionId,
    otherAnswerCode,
  } = useVerbatims({ cardType, questionMeta, activeIteration });

  const isFreeText = computed(() => isOpenQuestion.value || isMediaQuestion.value || isOpenRecode.value);
  const showOpenTabs = computed(() => (isOpenQuestion.value || isRecodeQuestion.value) && !isMediaQuestion.value);

  const configuring = computed(() => cardsStore.isConfiguring(props.card.id) && isRecodeQuestion.value);
  watch(configuring, (open) => {
    if (open && questionMeta.value?.token) recodesStore.initExistingRecode(questionMeta.value.token);
  });

  const mediaFilters = ref({});
  const downloadingMedia = computed(() =>
    taskProgresses.value.some(
      (t) =>
        t.taskInfo?.type === "media_archive" &&
        t.taskInfo?.questionId === verbatimsQuestionId.value &&
        !["Complete", "Failed"].includes(t.status),
    ),
  );

  async function downloadAllMedia() {
    if (downloadingMedia.value || !verbatimsQuestionId.value) return;
    const { response } = await useMyFetch(`/questions/${verbatimsQuestionId.value}/media`)
      .post({ client_id: getClientId(), filters: mediaFilters.value })
      .json();

    if (response.value.ok) captureEvent("verbatim_media_download_requested", { question_id: verbatimsQuestionId.value });
  }

  const codificationInProgress = computed(() => store.regeneratingCardIds.has(props.card.card_id));
  let codifPollInterval = null;
  let everSawCreating = false;

  async function fetchCodificationStatus() {
    const sourceQId = questionMeta.value?.source_question_id;
    if (!sourceQId) return null;
    try {
      const { data } = await useMyFetch(`/questions/${sourceQId}/tags_status`).get().json();
      return data.value?.status ?? null;
    } catch {
      return null;
    }
  }

  async function finishRegeneration() {
    try {
      await store.fetchAvailableCards();
      await store.refreshChartsData();
    } finally {
      store.clearCardRegenerating(props.card.card_id);
    }
  }

  async function startCodificationPolling({ optimistic = false } = {}) {
    if (codifPollInterval) clearInterval(codifPollInterval);
    everSawCreating = false;

    const initialStatus = await fetchCodificationStatus();
    if (initialStatus === "creating") {
      everSawCreating = true;
      store.markCardRegenerating(props.card.card_id);
    } else if (optimistic) {
      store.markCardRegenerating(props.card.card_id);
    } else if (codificationInProgress.value && initialStatus !== "creating") {
      await finishRegeneration();
      return;
    } else {
      return;
    }

    codifPollInterval = setInterval(async () => {
      if (!codificationInProgress.value) {
        clearInterval(codifPollInterval);
        return;
      }
      const status = await fetchCodificationStatus();
      if (status === "creating") {
        everSawCreating = true;
        return;
      }
      if (status === "created" || everSawCreating) {
        clearInterval(codifPollInterval);
        await finishRegeneration();
      }
    }, 5000);
  }

  if (isRecodeQuestion.value) {
    onMounted(() => startCodificationPolling());
    onUnmounted(() => clearInterval(codifPollInterval));
  }

  function updateLocalOption(key, value) {
    const current = props.card.options || {};
    cardsStore.updateCardLocal(props.card.id, {
      options: { ...current, [key]: value },
    });
  }

  const selectedLoopIndex = computed({
    get: () => props.card.options?.loop_index ?? loopIterationOptions.value[0]?.value ?? null,
    set: (val) => updateLocalOption("loop_index", val),
  });

  const isHalfWidth = computed(() => isHalfWidthCard(props.card, store.viewLayout));

  const freeTextMode = computed(() =>
    ["verbatims", "summary"].includes(mergedOptions.value.default_display)
      ? mergedOptions.value.default_display
      : "verbatims",
  );

  const displayMode = computed(() => {
    if (configuring.value) return "config";
    if (isFreeText.value) return freeTextMode.value;
    return chartDisplayMode.value;
  });
  const showVerbatimTranslations = ref(true);
  const { isMultiLocale } = storeToRefs(useSurveyLocalesStore());
  const showTranslationToggle = computed(
    () => displayMode.value === "verbatims" && !isMediaQuestion.value && isMultiLocale.value,
  );
  function setDisplayMode(mode) {
    updateLocalOption("default_display", mode);
  }

  const showCounts = computed(() => mergedOptions.value.show_counts ?? true);

  const { icon: cardTypeIcon, label: cardTypeLabel } = useCardTypeIdentity(cardRef);

  const activeLoopLabel = computed(() => {
    if (!activeIteration.value?.title) return null;
    const opt = loopIterationOptions.value.find((o) => o.value === selectedLoopIndex.value);
    return opt?.label || null;
  });

  const resolvedTitle = computed(() => {
    if (!displayTitle.value) return "";
    if (!activeLoopLabel.value) return displayTitle.value;
    return displayTitle.value.replace(/@loop_title/g, activeLoopLabel.value);
  });

  function onThemesApplied() {
    setDisplayMode("chart");
    store.refreshChartsData();
  }

  const summaryFilters = computed(() => viewPopulations.value?.[0]?.search_group?.filters || {});

  const summaryPopulationToken = ref(null);
  const summaryPopulation = computed(
    () =>
      displayedPopulations.value.find((pop) => pop.token === summaryPopulationToken.value) ||
      displayedPopulations.value[0] ||
      null,
  );
  const chartSummaryFilters = computed(() => {
    const pop = summaryPopulation.value;
    if (!pop || pop.token === "all") return {};
    return pop.filters || {};
  });
  const chartSummaryEmpty = ref(true);
  const chartSummaryExpanded = ref(false);
  const chartSummaryOverflows = ref(false);
  const showChartSummary = computed(
    () =>
      isRecodeQuestion.value &&
      !isOpenRecode.value &&
      (mergedOptions.value.show_summary ?? true) &&
      !!verbatimsQuestionId.value,
  );

  const isSelected = computed(() => cardsStore.selectedCard?.id === props.card.id);
  const barsClickable = computed(() => props.readonly || isSelected.value);

  const showPopulationSwitch = computed(
    () => isSingleSeries.value && displayMode.value === "chart" && displayedPopulations.value.length > 1,
  );
  const singlePopulationStackedData = computed(() =>
    stackedData.value.filter((row) => row.popId === singlePopulation.value?.id),
  );
  const singlePopulationBarData = computed(
    () => populationsBarData.value.find((pop) => pop.popId === singlePopulation.value?.id) || populationsBarData.value[0] || null,
  );
  const legendPopulations = computed(() =>
    showPopulationSwitch.value
      ? populationRespondents.value.filter((pop) => pop.token === singlePopulation.value?.token)
      : populationRespondents.value,
  );

  async function handleChartFilter({ questionId, code, popId }) {
    const qId = questionId ?? effectiveQuestionId.value;
    if (qId == null || code == null || String(code) === "999") return;
    const bucket = isNpsChartType.value ? NPS_BUCKETS.find((b) => b.code === code) : null;
    const { created } = populationsStore.toggleFilterOnPopulation({
      target: popId ?? "all",
      questionId: String(qId),
      code: bucket ? bucket.codes : String(code),
    });
    await store.fetchAllChartsData(viewPopulations.value);
    if (created) populationsStore.hideAllAfterQuickFilter();
  }
</script>

<template>
  <div class="group flex h-full flex-col gap-3 p-4">
    <!-- Header (hidden in config mode) -->
    <div
      v-if="displayMode !== 'config'"
      class="flex flex-col gap-2.5">
      <div class="flex items-start justify-between gap-2">
        <div class="flex flex-1 items-center gap-2 min-w-0">
          <CardTypeIcon
            :icon="cardTypeIcon"
            :label="cardTypeLabel" />
          <span
            :dir="textDirection"
            class="truncate"
            :class="resolvedTitle ? 'heading-label' : 'text-13 font-semibold text-primary'"
            >{{ displayName }}</span
          >
        </div>
        <div class="flex items-center gap-2 shrink-0">
          <div
            v-if="showPopulationSwitch"
            class="card-toolbar print:hidden">
            <PopulationSwitch
              :card="card"
              :populations="displayedPopulations"
              :selected="singlePopulation.token" />
          </div>
          <ResultsCardToolbar
            :card="card"
            :readonly="readonly"
            :display-mode="displayMode"
            :copied="copied"
            :show-verbatims="isFreeText && hasVerbatims"
            :show-summary="isFreeText && showOpenTabs"
            :show-copy="!isOpenQuestion"
            :show-download="!isOpenQuestion"
            :class="{ 'opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100': readonly }"
            @set-mode="setDisplayMode"
            @copy="copyCardToClipboard(card.id)"
            @download="downloadCardAsImage(card.id, displayName)">
            <Tooltip
              v-if="canAnalyzeSurvey && !isNumeric && isMediaQuestion"
              content="Download all"
              :delay-duration="1000">
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Download all media"
                class="text-muted-foreground"
                :disabled="downloadingMedia"
                @click="downloadAllMedia">
                <Download class="size-3.5" />
              </Button>
            </Tooltip>
          </ResultsCardToolbar>
          <span
            v-if="readonly && respondents && populationRespondents.length <= 1"
            class="whitespace-nowrap rounded-full bg-secondary px-2 py-0.5 text-2xs text-muted-foreground tabular-nums">
            n={{ respondents }}
            <ResultsV2CardIndicators
              :base-label="customBaseLabel"
              :weighted="shouldIncludeWeights" />
          </span>
        </div>
      </div>
      <div
        v-if="loopIterationOptions.length > 1"
        class="max-w-48">
        <Select
          v-model="selectedLoopIndex"
          :options="loopIterationOptions" />
      </div>
      <p
        v-if="resolvedTitle"
        :dir="textDirection"
        class="text-13 leading-snug font-semibold text-primary">
        {{ removeHtmlMarkdownAndLineBreak(resolvedTitle) }}
      </p>
      <div
        v-if="populationRespondents.length > 1 || (respondents && !readonly) || showTranslationToggle"
        class="mb-4 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <div
          v-if="populationRespondents.length > 1"
          class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground tabular-nums">
          <span
            v-for="pop in legendPopulations"
            :key="pop.token"
            class="inline-flex items-center gap-1">
            <span
              class="inline-block h-2 w-2 shrink-0 rounded-sm"
              :style="{ backgroundColor: pop.color }" />
            {{ pop.label }} (n={{ pop.respondents }})
          </span>
          <ResultsV2CardIndicators
            :base-label="customBaseLabel"
            :weighted="shouldIncludeWeights" />
        </div>
        <!-- In view mode the single-population base moves to the header chip -->
        <span
          v-else-if="respondents && !readonly"
          class="text-xs text-muted-foreground tabular-nums">
          n={{ respondents }}
          <ResultsV2CardIndicators
            :base-label="customBaseLabel"
            :weighted="shouldIncludeWeights" />
        </span>
        <ResultsVerbatimsLanguageToggle
          v-if="showTranslationToggle"
          v-model="showVerbatimTranslations"
          class="ml-auto" />
      </div>
    </div>

    <!-- Codification progress (hides chart/table/verbatims/summary while regenerating) -->
    <LoadingState
      v-if="codificationInProgress"
      label="Codification is in progress..." />

    <!-- Content -->
    <div
      v-else-if="questionMeta"
      :class="displayMode === 'verbatims' ? 'flex min-h-0 flex-1 flex-col' : ''">
      <div
        v-if="showChartSummary && ['chart', 'table'].includes(displayMode)"
        class="mb-4 rounded-lg bg-muted p-3"
        :class="{ 'card-toolbar print:hidden': chartSummaryEmpty }">
        <div class="mb-1.5 flex items-center justify-between gap-2">
          <div class="heading-label flex min-w-0 items-center gap-1.5 text-muted-foreground">
            <AiStar class="size-3 shrink-0" />
            <span class="shrink-0">AI summary ·</span>
            <DropdownMenu v-if="displayedPopulations.length > 1">
              <DropdownMenuTrigger
                class="flex min-w-0 items-center gap-1 uppercase hover:text-foreground"
                aria-label="Population of the AI summary">
                <span class="truncate">{{ summaryPopulation?.label || "All" }}</span>
                <ChevronDown class="card-toolbar size-3 shrink-0 print:hidden" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuRadioGroup
                  :model-value="summaryPopulation?.token"
                  @update:model-value="summaryPopulationToken = $event">
                  <DropdownMenuRadioItem
                    v-for="pop in displayedPopulations"
                    :key="pop.token"
                    :value="pop.token">
                    <span
                      class="inline-block size-2 shrink-0 rounded-sm"
                      :style="{ backgroundColor: pop.color }" />
                    {{ pop.label }}
                  </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
            <span
              v-else
              class="truncate">
              {{ summaryPopulation?.label || "All" }}
            </span>
          </div>
          <button
            v-if="chartSummaryOverflows || chartSummaryExpanded"
            type="button"
            class="card-toolbar text-xs font-medium text-muted-foreground hover:text-foreground print:hidden"
            @click="chartSummaryExpanded = !chartSummaryExpanded">
            {{ chartSummaryExpanded ? "Show less" : "Show more" }}
          </button>
        </div>
        <ResultsVerbatimSummary
          v-model:empty="chartSummaryEmpty"
          v-model:expanded="chartSummaryExpanded"
          v-model:overflows="chartSummaryOverflows"
          collapsible
          :question-id="verbatimsQuestionId"
          :summaries="questionMeta.verbatim_summaries || []"
          :filters="chartSummaryFilters" />
      </div>

      <!-- Numeric table -->
      <ResultsNumericTable
        v-if="isNumeric && numericStats"
        :populations="numericStats" />

      <!-- Verbatims list -->
      <div
        v-else-if="displayMode === 'verbatims'"
        class="flex min-h-0 flex-1 flex-col">
        <p
          v-if="!isOpenQuestion && !isRecodeQuestion"
          class="mb-3 text-xs font-medium">
          Other answers
        </p>
        <ResultsVerbatimsList
          class="min-h-0 flex-1"
          :question-id="verbatimsQuestionId"
          :card-type="cardType"
          :fill="readonly && isHalfWidth"
          :show-translations="showVerbatimTranslations"
          @filters-change="mediaFilters = $event" />
      </div>

      <!-- AI Summary -->
      <ResultsVerbatimSummary
        v-else-if="displayMode === 'summary'"
        :question-id="verbatimsQuestionId || questionMeta.question_id"
        :summaries="questionMeta.verbatim_summaries || []"
        :filters="summaryFilters" />

      <!-- Recode configuration -->
      <RecodeOpenEndForm
        v-else-if="displayMode === 'config' && questionMeta?.token"
        :card-token="questionMeta.token"
        @save="handleConfigSave"
        @cancel="handleConfigCancel"
        @delete="handleConfigDelete" />

      <!-- Themes editor -->
      <ResultsThemesForm
        v-else-if="displayMode === 'themes'"
        :question-id="verbatimsQuestionId"
        :choices="choices"
        @cancel="setDisplayMode('chart')"
        @applied="onThemesApplied" />

      <template v-else-if="displayMode === 'chart'">
        <!-- Matrix as bars: one bar chart per item -->
        <div
          v-if="isMatrix && isBar"
          class="flex flex-col gap-5">
          <div
            v-for="item in matrixItemsBarData"
            :key="item.itemId"
            class="flex flex-col gap-1.5">
            <span class="text-xs font-semibold text-foreground">
              {{ item.itemLabel }}
              <span class="font-normal text-muted-foreground">(N={{ item.itemN }})</span>
            </span>
            <ResultsBarChart
              :populations="item.populations"
              :horizontal="isHorizontal"
              :show-significance="showSignificance"
              :show-sentiment="showSentiment"
              :clickable="barsClickable"
              @bar-click="handleChartFilter({ ...$event, questionId: item.itemId })" />
          </div>
        </div>

        <!-- Bar / Column chart -->
        <ResultsBarChart
          v-else-if="isBar"
          :populations="populationsBarData"
          :horizontal="isHorizontal"
          :show-significance="showSignificance"
          :show-sentiment="showSentiment"
          :clickable="barsClickable"
          :collapsible="isRecodeQuestion"
          :verbatims-question-id="recodeQuestionId"
          :collapsible-code="otherAnswerCode"
          :collapsible-code-verbatims-question-id="otherQuestionId"
          :show-images="mergedOptions.show_images ?? false"
          @bar-click="handleChartFilter" />

        <!-- Stacked 100% (matrix) -->
        <ResultsStackedChart
          v-else-if="isStacked"
          :data="stackedData"
          :choice-codes="matrixChoiceCodes"
          :choice-labels="matrixChoiceLabels"
          :horizontal="isHorizontal"
          :show-significance="showSignificance"
          :show-nps-score="isNpsChartType"
          :clickable="barsClickable && cardType !== 'q_ranking'"
          @bar-click="handleChartFilter" />

        <!-- Pie / Doughnut (single series → filters the displayed population) -->
        <ResultsPieChart
          v-else-if="isPie"
          :data="pieData"
          :donut="isDoughnut"
          :clickable="barsClickable"
          @bar-click="handleChartFilter({ code: $event.code, popId: singlePopulation?.id })" />

        <!-- Heatmap (matrix, single series) -->
        <ResultsHeatmapChart
          v-else-if="isHeatmap"
          :data="singlePopulationStackedData"
          :choice-codes="matrixChoiceCodes"
          :choice-labels="matrixChoiceLabels"
          :accent="mergedOptions.color || singlePopulation?.color" />

        <!-- Podium (top 3 answers, single series) -->
        <ResultsPodiumChart
          v-else-if="isPodium"
          :population="singlePopulationBarData" />

        <ResultsDotPlotChart
          v-else-if="isDotPlot"
          :populations="populationsBarData"
          :show-significance="showSignificance" />

        <!-- Radar (matrix, one polygon per population) -->
        <template v-else-if="isRadar">
          <ResultsRadarChart
            v-if="radarAxes.length >= 3"
            :axes="radarAxes"
            :series="radarSeries"
            :min="radarScale.min"
            :max="radarScale.max" />
          <p
            v-else
            class="flex h-40 items-center justify-center text-center text-13 text-muted-foreground">
            A radar needs a matrix with at least 3 items.
          </p>
        </template>
      </template>

      <!-- Table view -->
      <ResultsDataTable
        v-else
        :populations="populationsBarData"
        :matrix-items="matrixTableData"
        :show-significance="showSignificance"
        :show-counts="showCounts"
        :clickable="barsClickable"
        :question-id="effectiveQuestionId"
        @bar-click="handleChartFilter" />
    </div>

    <!-- Comment -->
    <ResultsStyledBlock
      v-if="cardComment"
      :html="cardComment"
      :variant="mergedOptions.comment_style || 'info'"
      class="mt-4" />

    <!-- Fallback -->
    <div
      v-else-if="!questionMeta"
      class="flex h-32 items-center justify-center text-13 text-muted-foreground">
      Question not found ({{ card.card_id }})
    </div>
  </div>
</template>
